import React, { useEffect, useState } from 'react';
import { PollCard } from './PollCard';
import { ArrowLeft, ChevronDown, Filter, PlusCircle, Search, SlidersHorizontal, Trophy } from 'lucide-react';
interface PollFeedProps {
  onBack?: () => void;
  onCreatePoll?: () => void;
  showRitualPollsOnly?: boolean;
  ritualId?: string;
}
export const PollFeed: React.FC<PollFeedProps> = ({
  onBack,
  onCreatePoll,
  showRitualPollsOnly = false,
  ritualId
}) => {
  const [polls, setPolls] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'ended' | 'ritual'>(showRitualPollsOnly ? 'ritual' : 'all');
  useEffect(() => {
    // Simulate fetching polls from API
    const fetchPolls = async () => {
      setLoading(true);
      // In a real app, you would fetch from your API
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Mock data
      const mockPolls = [{
        id: 'poll1',
        question: "What's your favorite feature in the app?",
        options: [{
          id: 'opt1',
          text: 'Angel Chat'
        }, {
          id: 'opt2',
          text: 'Social Feed'
        }, {
          id: 'opt3',
          text: 'Rituals & Rewards'
        }, {
          id: 'opt4',
          text: 'AI Image Generation'
        }],
        createdAt: new Date(Date.now() - 3600000 * 5).toISOString(),
        endsAt: new Date(Date.now() + 3600000 * 19).toISOString(),
        totalVotes: 342,
        createdBy: {
          name: 'Harper',
          avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bZ3iVXv5nZEMY6F847weq5/harper.png',
          verified: true
        },
        isRitual: true,
        ritualId: 'ritual1'
      }, {
        id: 'poll2',
        question: 'What type of content would you like to see more of?',
        options: [{
          id: 'opt1',
          text: 'Behind-the-scenes photos'
        }, {
          id: 'opt2',
          text: 'Personal stories'
        }, {
          id: 'opt3',
          text: 'Q&A sessions'
        }, {
          id: 'opt4',
          text: 'Interactive games'
        }],
        createdAt: new Date(Date.now() - 3600000 * 24).toISOString(),
        endsAt: new Date(Date.now() + 3600000 * 48).toISOString(),
        totalVotes: 189,
        createdBy: {
          name: 'Zoey',
          avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fnQWPJWMus62iBnJCYdU4W/z1.png',
          verified: true
        },
        isRitual: false
      }, {
        id: 'poll3',
        question: "What outfit should I wear for tomorrow's photoshoot?",
        options: [{
          id: 'opt1',
          text: 'Red dress'
        }, {
          id: 'opt2',
          text: 'Blue jeans and white top'
        }, {
          id: 'opt3',
          text: 'Black leather jacket'
        }],
        createdAt: new Date(Date.now() - 3600000 * 2).toISOString(),
        endsAt: new Date(Date.now() + 3600000 * 10).toISOString(),
        totalVotes: 576,
        createdBy: {
          name: 'Sophia',
          avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bZ3iVXv5nZEMY6F847weq5/sophia.png',
          verified: true
        },
        isRitual: true,
        ritualId: 'ritual2'
      }, {
        id: 'poll4',
        question: "What's your preferred time for live streams?",
        options: [{
          id: 'opt1',
          text: 'Morning (8-11 AM)'
        }, {
          id: 'opt2',
          text: 'Afternoon (1-4 PM)'
        }, {
          id: 'opt3',
          text: 'Evening (7-10 PM)'
        }, {
          id: 'opt4',
          text: 'Late night (11 PM-2 AM)'
        }],
        createdAt: new Date(Date.now() - 3600000 * 48).toISOString(),
        endsAt: new Date(Date.now() - 3600000 * 24).toISOString(),
        totalVotes: 892,
        userVote: 'opt3',
        createdBy: {
          name: 'Kim Holland',
          avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bZ3iVXv5nZEMY6F847weq5/kimholland.png',
          verified: true
        },
        isRitual: false
      }];
      // Filter based on the selected filter and ritualId if provided
      let filteredPolls = mockPolls;
      if (showRitualPollsOnly) {
        filteredPolls = mockPolls.filter(poll => poll.isRitual);
      }
      if (ritualId) {
        filteredPolls = filteredPolls.filter(poll => poll.ritualId === ritualId);
      }
      if (filter === 'active') {
        filteredPolls = filteredPolls.filter(poll => new Date(poll.endsAt) > new Date());
      } else if (filter === 'ended') {
        filteredPolls = filteredPolls.filter(poll => new Date(poll.endsAt) <= new Date());
      } else if (filter === 'ritual') {
        filteredPolls = filteredPolls.filter(poll => poll.isRitual);
      }
      setPolls(filteredPolls);
      setLoading(false);
    };
    fetchPolls();
  }, [filter, showRitualPollsOnly, ritualId]);
  const handleVote = (pollId: string, optionId: string) => {
    // In a real app, you would send this to your API
    console.log(`Voted for option ${optionId} in poll ${pollId}`);
    // Update the local state to reflect the vote
    setPolls(polls.map(poll => {
      if (poll.id === pollId) {
        return {
          ...poll,
          userVote: optionId,
          totalVotes: poll.totalVotes + 1
        };
      }
      return poll;
    }));
  };
  return <div className="w-full max-w-2xl mx-auto" data-id="element-15543">
      <div className="sticky top-0 z-10 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-15544">
        <div className="flex items-center justify-between mb-4" data-id="element-15545">
          {onBack ? <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" data-id="element-15546">
              <ArrowLeft size={20} className="text-gray-700 dark:text-gray-300" data-id="element-15547" />
            </button> : <div data-id="element-15548"></div>}
          <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-15549">
            {showRitualPollsOnly ? <>
                <Trophy size={20} className="text-pink-500" data-id="element-15550" />
                Ritual Polls
              </> : 'Community Polls'}
          </h1>
          {onCreatePoll && <button onClick={onCreatePoll} className="p-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:shadow-md hover:shadow-pink-500/20 transition-all" data-id="element-15551">
              <PlusCircle size={20} data-id="element-15552" />
            </button>}
        </div>
        <div className="flex items-center gap-2" data-id="element-15553">
          <div className="relative flex-1" data-id="element-15554">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-15555" />
            <input type="text" placeholder="Search polls..." className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent" data-id="element-15556" />
          </div>
          <div className="flex items-center" data-id="element-15557">
            <button className="p-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700" data-id="element-15558">
              <SlidersHorizontal size={16} data-id="element-15559" />
            </button>
            <div className="relative ml-2" data-id="element-15560">
              <button className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-1" data-id="element-15561">
                <span data-id="element-15562">
                  {filter === 'all' ? 'All Polls' : filter === 'active' ? 'Active' : filter === 'ended' ? 'Ended' : 'Ritual Polls'}
                </span>
                <ChevronDown size={14} data-id="element-15563" />
              </button>
              <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 z-10 hidden" data-id="element-15564">
                <button className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-t-lg" onClick={() => setFilter('all')} data-id="element-15565">
                  All Polls
                </button>
                <button className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => setFilter('active')} data-id="element-15566">
                  Active
                </button>
                <button className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => setFilter('ended')} data-id="element-15567">
                  Ended
                </button>
                <button className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-b-lg" onClick={() => setFilter('ritual')} data-id="element-15568">
                  Ritual Polls
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4" data-id="element-15569">
        {loading ? <div className="space-y-4" data-id="element-15570">
            {[1, 2, 3].map(i => <div key={i} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden animate-pulse" data-id="element-15571">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center gap-3" data-id="element-15572">
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700" data-id="element-15573"></div>
                  <div className="flex-1" data-id="element-15574">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2" data-id="element-15575"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/4" data-id="element-15576"></div>
                  </div>
                </div>
                <div className="p-4" data-id="element-15577">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4" data-id="element-15578"></div>
                  <div className="space-y-2 mb-4" data-id="element-15579">
                    {[1, 2, 3].map(j => <div key={j} className="h-10 bg-gray-200 dark:bg-gray-700 rounded" data-id="element-15580"></div>)}
                  </div>
                  <div className="flex justify-between" data-id="element-15581">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4" data-id="element-15582"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/5" data-id="element-15583"></div>
                  </div>
                </div>
              </div>)}
          </div> : polls.length > 0 ? <div className="space-y-4" data-id="element-15584">
            {polls.map(poll => <PollCard key={poll.id} id={poll.id} question={poll.question} options={poll.options} createdAt={poll.createdAt} endsAt={poll.endsAt} totalVotes={poll.totalVotes} userVote={poll.userVote} createdBy={poll.createdBy} isRitual={poll.isRitual} ritualId={poll.ritualId} onVote={handleVote} data-id="element-15585" />)}
          </div> : <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center" data-id="element-15586">
            <div className="w-16 h-16 mx-auto flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full mb-4" data-id="element-15587">
              <Filter size={24} className="text-gray-400 dark:text-gray-600" data-id="element-15588" />
            </div>
            <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white" data-id="element-15589">
              No polls found
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto" data-id="element-15590">
              {filter === 'ritual' ? 'There are no ritual polls available right now. Complete rituals to see more polls.' : 'There are no polls matching your current filter. Try changing your filter or create a new poll.'}
            </p>
            {onCreatePoll && <button onClick={onCreatePoll} className="mt-4 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 rounded-lg text-white font-medium hover:shadow-md hover:shadow-pink-500/20 transition-all flex items-center gap-2 mx-auto" data-id="element-15591">
                <PlusCircle size={16} data-id="element-15592" />
                <span data-id="element-15593">Create a Poll</span>
              </button>}
          </div>}
      </div>
    </div>;
};